/**
 * Flattens permission keys from the access context returned by
 * `GET {GINJA}/me/access` → `data.departments[].features[].permissions[]`.
 * Features with an empty `permissions` array do not grant keys here; the API
 * should emit permission keys for any feature that must gate the UI.
 */
export function buildPermissionKeyList(context) {
  if (!context?.departments || !Array.isArray(context.departments)) return [];
  const keys = [];
  for (const d of context.departments) {
    for (const f of d.features || []) {
      for (const p of f.permissions || []) {
        if (p?.key) keys.push(p.key);
      }
    }
  }
  return keys;
}

export function getStoredAccessContext() {
  if (typeof localStorage === "undefined") return null;
  const raw = localStorage.getItem("accessContext");
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function getUserFromStorage() {
  if (typeof localStorage === "undefined") return null;
  const raw = localStorage.getItem("userInfo");
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Gating: only enforce when a successful /me/access object was stored (has `user`).
 * Before bootstrap, avoid blocking the tree.
 */
export function isAccessContextEnforced() {
  const c = getStoredAccessContext();
  return !!(c && typeof c === "object" && c.user);
}

export function isUserPrivileged() {
  return !!getUserFromStorage()?.is_privileged;
}

/**
 * @param {string[]} permissionKeys
 * @param {string[]|null|undefined} requiredAny
 */
export function hasAnyPermission(permissionKeys, requiredAny) {
  if (!Array.isArray(requiredAny) || requiredAny.length === 0) return true;
  if (!isAccessContextEnforced()) return true;
  const have = new Set(permissionKeys || []);
  return requiredAny.some((k) => have.has(k));
}

/**
 * Route / nav check.
 * - requirePrivileged: only `user.is_privileged` (e.g. Users)
 * - requiredAny: need at least one of these keys
 * - orPrivilege: additionally allow privileged users (e.g. Audit: IT key OR chief)
 * - if requiredAny is empty/undefined and not requirePrivileged, allow
 */
export function canAccessRoute({
  permissionKeys = [],
  requiredAny,
  requirePrivileged = false,
  orPrivilege = false,
}) {
  if (!isAccessContextEnforced()) return true;
  if (requirePrivileged) return isUserPrivileged();
  if (orPrivilege && isUserPrivileged()) return true;
  if (!Array.isArray(requiredAny) || requiredAny.length === 0) return true;
  return hasAnyPermission(permissionKeys, requiredAny);
}